import { Component, computed, effect, ElementRef, inject, input, signal, viewChild } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { CardViewService, DashboardsService, IFirmUsersService, AnalyticsService } from "@app/shared";
import { CardDisplayService } from "@app/shared/card-display/card-display.service";
import { JobsAndBillingJobTypesService } from "@app/shared/jobs-and-billing-job-types-service/jobs-and-billing-job-types.service";
import { JobsBillingCard } from "@app/views-interfaces";
import { TranslateService, TranslatePipe } from "@ngx-translate/core";
import { format, fromUnixTime } from "date-fns";
import { UserDataModel } from "src/app/settings-interfaces/users.models";
import { JumpstartComponentsModule } from "@wk/components-v3-angular17";
import { NgClass, DatePipe } from "@angular/common";
import { CardTitlePipe } from "../../shared/card-pipe/card-title.pipe";
import { UserInitialPipe } from "../card-t1-component/UserInitialPipe/user-initial.pipe";
import { UserNamePipe } from "../card-t1-component/UserNamePipe/user-name.pipe";
import { LanguageService } from "@app/shared/services";
import { DATE_FORMAT } from "@shared/date-format.constants";

@Component({
    selector: "wk-card-jobs-billing",
    templateUrl: "./card-jobs-billing.component.html",
    styleUrls: ["../card-t1-component/card-t1.component.scss", "./card-jobs-billing.component.scss"],
    host: {
        class: "wk-card-t1 wk-card-job",
        "[id]": "divId()",
        "(click)": "openCard($event)",
        "(mouseenter)": "onMouseEnter()",
        "(mouseleave)": "onMouseLeave()",
        "[class.wk-card-t1__column]": "format() === 'column'",
        "[class.wk-card-t1__grid]": "format() === 'grid'",
        "[class.row]": "format() === 'grid'",
        "[class.wk-card-t1__column--quiet]": "format() === 'column' && quietDaysFromNow > 0",
        "[class.wk-card-t1__column--danger]": "this.cardViewService.currentViewType !== 'completed' && format() === 'column' && (isStepAlert)",
        "[class.wk-card-t1__column--warning]":
            "this.cardViewService.currentViewType !== 'completed' && format() === 'column' && (isStepWarning && !isStepAlert)",
        "[class.notStarted]": "format() === 'grid' && cardViewService.currentViewType === 'notStarted'",
        "[class.completed]": "format() === 'grid' && cardViewService.currentViewType === 'completed'",
    },
    imports: [JumpstartComponentsModule, NgClass, DatePipe, CardTitlePipe, TranslatePipe, UserInitialPipe, UserNamePipe],
})
export class CardJobsBillingComponent {
    private readonly router = inject(Router);
    private readonly jobsAndBillingJobTypesService = inject(JobsAndBillingJobTypesService);
    private readonly translateService = inject(TranslateService);
    private readonly cardDisplayService = inject(CardDisplayService);
    private readonly dashboardsService = inject(DashboardsService);
    public readonly cardViewService = inject(CardViewService);
    private readonly iFirmUsersService = inject(IFirmUsersService);
    private readonly analyticsService = inject(AnalyticsService);
    private readonly languageService = inject(LanguageService);

    protected tooltipDisabled = signal(true);
    protected isHovered = signal(false);

    protected tooltipText = computed(() => () => this.column_body_date_tooltip());
    private readonly cardJobColumnName = viewChild<ElementRef<HTMLElement>>("cardJobColumnName");
    protected assignedUser: UserDataModel | null = null;

    protected isDeadlineWarning = false;
    protected isDeadlineAlert = false;
    protected isStepWarning = false;
    protected isStepAlert = false;
    private isTodayDeadlineDate = false;
    protected deadlineDate: string | null = null;
    private deadlineDaysFromNow: number | null = null;
    private deadlineDaysFromNow_Absolute: number | null = null;
    protected daysSinceInStep = 0;
    protected daysSinceReady = 0;
    protected quietDaysFromNow = 0;
    protected currentLanguage = this.languageService.language;
    protected readonly dateFormat = DATE_FORMAT;
    private deadlineTip_Warning = false;
    private deadlineTip_Alert = false;
    private deadlineTip_Critical = false;

    format = input("column");
    statusIds = input<string[]>();
    cardData = input.required<JobsBillingCard>();
    public matchedField = input<string>("");
    protected isFromSearch = input<boolean>(false);

    protected readonly divId = computed(() => {
        const cardData = this.cardData();

        if (!cardData?.cardId) return "";

        return "div_" + cardData.cardId.replaceAll(/[{}]/g, "");
    });

    protected jobTypeShortCode = computed(() => {
        const cardData = this.cardData();

        if (!cardData?.job) return null;

        return this.jobsAndBillingJobTypesService.jobTypes().get(cardData.job?.typeId)?.shortCode ?? null;
    });

    constructor() {
        this.cardDisplayService.calcCardFieldLabelsEvent$.pipe(takeUntilDestroyed()).subscribe((data) => {
            if (this.cardData()?.cardId === data.cardId) {
                this.calcAllFieldLabels();
            }
        });

        effect(() => {
            const data = this.cardData();
            const statusIds = this.statusIds();

            if (!data || !statusIds) return;

            this.getAssignedUser();
            this.calcAllFieldLabels();
        });

        this.languageService.langChangeEvent$.pipe(takeUntilDestroyed()).subscribe(() => {
            this.calcAllFieldLabels(true);
        });

        this.cardViewService.refreshCardsViewLabelsEvent$.pipe(takeUntilDestroyed()).subscribe(() => {
            this.calcAllFieldLabels();
        });

        effect(() => {
            const cardJobColumnName = this.cardJobColumnName();
            if (!cardJobColumnName) return;
            this.tooltipDisabled.set(cardJobColumnName?.nativeElement.scrollWidth <= cardJobColumnName?.nativeElement.clientWidth);
        });
    }

    calcAllFieldLabels(skipCache = false) {
        const result = this.cardDisplayService.calcCardFieldLabels(this.cardData(), this.statusIds(), skipCache);

        this.deadlineDaysFromNow = result.deadlineDaysFromNow;
        this.deadlineDate = result.deadlineDate;
        this.daysSinceInStep = result.daysSinceInStep;
        this.daysSinceReady = result.daysSinceReady;
        this.isStepWarning = result.isStepWarning;
        this.isStepAlert = result.isStepAlert;
        this.isDeadlineWarning = result.isDeadlineWarning;
        this.isDeadlineAlert = result.isDeadlineAlert;
        this.quietDaysFromNow = result.quietDaysFromNow;

        this.setDeadlineTooltip();
        this.deadlineDaysFromNow_Absolute = Math.abs(this.deadlineDaysFromNow);
        this.isTodayDeadlineDate = this.isDeadlineDateToday(this.cardData(), this.deadlineDaysFromNow);
    }

    protected readonly cardTitle = computed(() => {
        const contactInformation = this.cardData()?.contactInformation;
        const name = [contactInformation?.firstName, contactInformation?.lastName].filter(Boolean).join(" ");
        return name || this.cardData()?.name || "";
    });

    protected openCard(e: MouseEvent): void {
        e.preventDefault();
        if (this.isFromSearch()) {
            const matchedField = this.matchedField();
            this.analyticsService.track("OnePageApp : Opening a card after a search", { matchedField });
        }
        this.router.navigate(["/" + this.router.url, this.cardData()?.cardId]);
    }

    onMouseEnter(): void {
        this.isHovered.set(true);
    }
    onMouseLeave(): void {
        this.isHovered.set(false);
    }

    private async getAssignedUser() {
        this.assignedUser = null;
        const users = (await this.iFirmUsersService.getUsers()).filter((it) => it.Active && it.LicenseEnabled);

        for (const user of users) {
            if (user.UserId.toString() === this.cardData()?.job?.currentlyResponsible) {
                this.assignedUser = {
                    FirstName: user.FirstName,
                    LastName: user.LastName,
                } as UserDataModel;
                break;
            }
        }
    }

    private column_body_date_tooltip(): string {
        if (!this.isDeadlineWarning && !this.isDeadlineAlert) {
            return this.translateService.instant("JobsAndBilling.TargetEndDate");
        }

        if (this.deadlineTip_Warning && !this.deadlineTip_Critical && !this.deadlineTip_Alert) {
            return this.buildDeadlineTooltip("DeadlineWarning", "");
        }
        if (this.deadlineTip_Alert && !this.deadlineTip_Critical && !this.deadlineTip_Warning && this.isTodayDeadlineDate) {
            return this.buildDeadlineTooltip("DeadlineAlert", "TimeDuration2");
        }
        if (this.deadlineTip_Alert && !this.deadlineTip_Critical && !this.deadlineTip_Warning && !this.isTodayDeadlineDate) {
            return this.buildDeadlineTooltip("DeadlineAlert", "TimeDuration1");
        }
        if (this.deadlineTip_Critical && !this.deadlineTip_Warning && !this.deadlineTip_Alert) {
            return this.buildDeadlineTooltip("DeadlineCritical", "");
        }
        return "";
    }

    private buildDeadlineTooltip(
        tooltipType: "DeadlineWarning" | "DeadlineAlert" | "DeadlineCritical",
        durationKey: "" | "TimeDuration1" | "TimeDuration2",
    ): string {
        const tooltipPrefix = "JobsAndBilling.Tooltips.";
        const dayKey = this.deadlineDaysFromNow_Absolute === 0 || this.deadlineDaysFromNow_Absolute === 1 ? "AverageDay" : "AverageDays";
        const duration = durationKey ? ` ${this.translateService.instant(tooltipPrefix + durationKey)}` : "";

        return (
            this.translateService.instant(tooltipPrefix + tooltipType + "1") +
            " " +
            this.deadlineDaysFromNow_Absolute +
            this.translateService.instant(tooltipPrefix + dayKey) +
            duration +
            this.translateService.instant(tooltipPrefix + tooltipType + "2")
        );
    }

    setDeadlineTooltip(): boolean {
        this.deadlineTip_Warning = false;
        this.deadlineTip_Alert = false;
        this.deadlineTip_Critical = false;
        if (this.dashboardsService.currentDashboard()?.DaysBeforeDeadlineWarning != null) {
            if (this.deadlineDate != null) {
                if (this.deadlineDaysFromNow) {
                    if (
                        this.deadlineDaysFromNow < 0 &&
                        Math.abs(this.deadlineDaysFromNow) > this.dashboardsService.currentDashboard()?.DaysBeforeDeadlineWarning
                    ) {
                        return true;
                    } else if (
                        this.deadlineDaysFromNow < 0 &&
                        this.dashboardsService.currentDashboard()?.DaysBeforeDeadlineWarning >= Math.abs(this.deadlineDaysFromNow) &&
                        Math.abs(this.deadlineDaysFromNow) > this.dashboardsService.currentDashboard()?.DaysBeforeDeadlineAlert
                    ) {
                        this.deadlineTip_Warning = true;
                        return true;
                    } else if (
                        this.deadlineDaysFromNow < 0 &&
                        this.dashboardsService.currentDashboard()?.DaysBeforeDeadlineAlert >= Math.abs(this.deadlineDaysFromNow)
                    ) {
                        this.deadlineTip_Alert = true;
                        return true;
                    } else if (this.deadlineDaysFromNow > 0) {
                        this.deadlineTip_Critical = true;
                        return true;
                    }
                } else if (this.deadlineDaysFromNow == 0 && this.deadlineDate != null) {
                    this.deadlineTip_Alert = true;
                    return true;
                }
            } else {
                return true;
            }
        } else {
            return true;
        }
    }

    isDeadlineDateToday(cardData: JobsBillingCard, dealineDays: number) {
        if (dealineDays < 0) {
            return true;
        } else if (cardData.job?.targetEndDate) {
            return format(fromUnixTime(cardData.job.targetEndDate), "MMMM d, yyyy") === format(Date.now(), "MMMM d, yyyy");
        } else {
            return false;
        }
    }
}
